<template>
  <div class="synthese__bloc">
    <div class="col-3"></div>
    <SyntheseSection
      title="L'employeur"
      :items="employerDetails"
      class="col-14 col-sm-36"
    />
    <div class="col-2"></div>
    <SyntheseSection
      title="L'apprenti(e)"
      :items="apprenticeDetails"
      class="col-14 col-sm-36"
    />
    <div class="col-3"></div>
  </div>
  <div class="synthese__bloc">
    <div class="col-3"></div>
    <SyntheseSection
      title="Maître d'apprentissage 1"
      :items="premierMaitreAppDetail"
      class="col-14 col-sm-36"
    />
    <div class="col-2"></div>
    <SyntheseSection
      title="Maître d'apprentissage 2"
      :items="secondMaitreAppDetail"
      class="col-14 col-sm-36"
    />
    <div class="col-3"></div>
  </div>

  <div class="synthese__bloc">
    <div class="col-3"></div>
    <SyntheseSection
      title="Le contrat"
      :items="contratDetail"
      class="col-14 col-sm-36"
    />
    <div class="col-2"></div>
    <SyntheseSection
      title="Formation"
      :items="formationDetail"
      class="col-14 col-sm-36"
    />
    <div class="col-3"></div>
  </div>
  <FormContainer>
    <template #content>
      <div class="col-36">
        <WrapperCheckbox
          id="field_unknown1"
          v-model="attestationSurHonneurModel"
          :alertLabel="meta.attestationSurHonneur?.$messageErreurDirty!"
          label="J’atteste sur l’honneur l’exactitude des données saisies."
          :readonly
          class="wrapper-width-fit"
        />
      </div>
      <div class="col-36">
        <WrapperCheckbox
          id="field_unknown2"
          v-model="acceptationCgModel"
          :alertLabel="meta.acceptationCg?.$messageErreurDirty!"
          :readonly
        >
          <BasicText style="flex: 1">
            J'accepte les Conditions Générales (CG) d'Akto et les conditions de prise en charge, et je confirme avoir
            pris connaissance de celles-ci via le lien suivant : «
            <BasicLink
              label="Conditions générales - Akto"
              @link-click="redirectExterne('https://www.akto.fr/conditions-generales')"
            />
          </BasicText>
          »
        </WrapperCheckbox>
      </div>
      <div class="form__bloc">
        <WrapperInput
          id="field_240"
          v-model="lieuSignatureModel"
          label="Fait à"
          :required="meta.lieuSignature?.$status === PropertyStatus.Required"
          :alertLabel="meta.lieuSignature?.$messageErreurDirty!"
          class="col-12 col-md-14 col-sm-36"
          deletable
          hint="50 caractères maximum"
          :maxlength="50"
          :readonly
        />
        <WrapperInput
          id="field_unknown"
          v-model="commentaireModel"
          label="Commentaires (infos complémentaires...)"
          :required="meta.commentaire?.$status === PropertyStatus.Required"
          :alertLabel="meta.commentaire?.$messageErreurDirty!"
          class="col-24 col-md-22 col-sm-36"
          deletable
          hint="256 caractères maximum"
          :maxlength="256"
          :readonly
        />
      </div>
    </template>
  </FormContainer>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { storeToRefs } from 'pinia'
  import type { LovDTO } from '@/features/api/extranet/dto/Lov'
  import { PropertyStatus } from '@/features/domaine/property-status/PropertyStatus'
  import { useDemandeCAPPStore } from '@/features/interface/demande/capp/useDemandeCAPPStore'
  import { FormContainer, SyntheseSection } from '@/features/interface/demande/shared'
  import { dateFormatter, isDate, isNullOrUndefined } from '@/features/shared/tools'
  import { inputDureeToString } from '@designSystem/components/basic/inputDuree/IInputDuree'
  import { redirectExterne } from '@/features/shared/tools/url'

  const store = useDemandeCAPPStore()
  const { setupFieldWithKey } = store
  const { meta, readonly } = storeToRefs(store)

  const lieuSignatureModel = setupFieldWithKey<string>('lieuSignature', true)
  const commentaireModel = setupFieldWithKey<string>('commentaire', true)
  const attestationSurHonneurModel = setupFieldWithKey<boolean>('attestationSurHonneur')
  const acceptationCgModel = setupFieldWithKey<boolean>('acceptationCg')
  // TODO : prévoir un composant pour afficher une liste (rémunération)

  const { model, lov } = useDemandeCAPPStore()

  const getLovIntitule = <T extends string | number>(lovDTO: LovDTO<T>, code?: T | null) => {
    if (isNullOrUndefined(code)) return ''
    return lovDTO.filter((item) => item.code === code).at(0)?.intitule ?? ''
  }

  const getDynamicLovIntitule = <T extends string | number>(lovDTO: LovDTO<T>, code: T | null | undefined) => {
    const lovIntituleWithCode = getLovIntitule(lovDTO, code)
    return lovIntituleWithCode.split('-').at(1)
  }

  const getDateIntitule = (value: any) => (isDate(value) ? dateFormatter(value) : '')

  const employerDetails = computed(() => ({
    'Nom et prénom ou dénomination': model?.employeur?.etablissement?.denomination,
    Siret: model?.employeur?.etablissement?.siret?.valeur,
    'IDCC applicable': model?.employeur?.etablissement?.idcc?.code,
    Email: model?.employeur?.contact?.email,
    'N° et Voie': model?.employeur?.etablissement?.adresse?.voie,
    Complément: model?.employeur?.etablissement?.adresse?.complement,
    'CP Ville': `${model?.employeur?.etablissement?.adresse?.codePostal} ${model?.employeur?.etablissement?.adresse?.commune} `,
  }))

  const apprenticeDetails = computed(() => ({
    "Nom de naissance de l'apprenti(e)": model?.apprenti?.nomNaissance,
    "Nom d'usage": model?.apprenti?.nom,
    Prénom: model?.apprenti?.prenom,
    'Date de naissance': getDateIntitule(model?.apprenti?.dateNaissance),
    email: model?.apprenti?.email,
  }))

  const premierMaitreAppDetail = computed(() => ({
    Nom: model?.tutorat?.maitreApprentissage?.nomNaissance,
    Prénom: model?.tutorat?.maitreApprentissage?.prenom,
    'Date de naissance': getDateIntitule(model?.tutorat?.maitreApprentissage?.dateNaissance),
  }))

  const secondMaitreAppDetail = computed(() => ({
    Nom: model?.tutorat?.maitreApprentissageSecondaire?.nomNaissance,
    Prénom: model?.tutorat?.maitreApprentissageSecondaire?.prenom,
    'Date de naissance': getDateIntitule(model?.tutorat?.maitreApprentissageSecondaire?.dateNaissance),
  }))

  const contratDetail = computed(() => ({
    'Type de contrat ou d’avenant': getDynamicLovIntitule(lov.TypeContratAvenant, model?.contrat?.typeContratAvenant),
    'Si avenant, date d’effet': getDateIntitule(model?.contrat?.dateEffetAvenant),
    'Type de dérogation': getDynamicLovIntitule(lov.TypeDerogation, model?.contrat?.typeDerogation),
    'Date de conclusion': getDateIntitule(model?.contrat?.dateConclusion),
    'Date de début d’exécution du contrat': getDateIntitule(model?.contrat?.dateDebutExecutionContrat),
    'Date de début de formation pratique chez l’employeur': getDateIntitule(
      model?.contrat?.dateDebutFormationChezEmployeur,
    ),
    "Date de fin du contrat ou de la période d'apprensissage": getDateIntitule(
      model?.contrat?.dateFinContratOuPeriodeApprentissage,
    ),
    'Durée hebdomadaire du travail': inputDureeToString(model?.contrat?.dureeHebdomadaire),
  }))

  const formationDetail = computed(() => {
    return {
      'Dénomination du CFA responsable': model?.formation?.cfaEtablissement?.denomination,
      'N° UAI': model?.formation?.cfaEtablissement?.uai,
      'N° SIRET du CFA': model?.formation?.cfaEtablissement?.siret.valeur,
      'Adresse du CFA':
        model?.formation?.cfaEtablissement?.adresse?.voie +
        ' ' +
        (model?.formation?.cfaEtablissement?.adresse?.complement ?? '') +
        ' ' +
        model?.formation?.cfaEtablissement?.adresse?.codePostal +
        ' ' +
        model?.formation?.cfaEtablissement?.adresse?.commune,
      'Diplôme ou titre visé par l’apprenti': getLovIntitule(lov.Diplome, model?.formation?.diplome),
      'Code RNCP': model?.formation?.rncp?.code,
      'Code Diplôme': model?.formation?.codeDiplome?.code,
      'Intitulé précis du diplôme/titre visé':
        model?.formation?.typeQualification === 'Rncp'
          ? model?.formation?.rncp?.intitule
          : model?.formation?.codeDiplome?.intitule,
      'Date de début de formation en CFA': getDateIntitule(model?.formation?.dateDebutFormationCfa),
      'Date prévue de fin des épreuves ou examens': getDateIntitule(model?.formation?.datePrevueFinEpreuvesOuExamens),
      'Durée de la formation': model?.formation?.dureeFormationEnHeures,
    }
  })
</script>
<style scoped lang="less">
  .synthese {
    &__bloc {
      margin: @spacing-20 0;
      display: grid;
      grid-template-columns: repeat(36, 1fr);
      grid-row-gap: @spacing-20;
      grid-column-gap: 2%;
    }
  }
</style>
